import React, { useState } from "react";
import ToolLayout from "@/components/ToolLayout";
import PostResultCTA from "@/components/PostResultCTA";
import useSEO from "@/hooks/useSEO";

const SOURCE_CLASSES = [
  { label: "Class 1 — Company-owned content (policies, KB, macros)", value: "1", score: 0 },
  { label: "Class 2 — Operational patterns (tickets, resolved cases)", value: "2", score: 15 },
  { label: "Class 3 — Inferred or implicit patterns", value: "3", score: 25 },
];

function calcRisk({ ageMonths, sourceClass, deploymentCount, monthsSinceReview, hasPii }) {
  let total = 0;
  const breakdown = [];

  // Age factor
  const ageScore =
    ageMonths <= 12 ? 0 : ageMonths <= 24 ? 15 : ageMonths <= 36 ? 25 : 35;
  total += ageScore;
  breakdown.push({ factor: "Age", value: `${ageMonths} months`, score: ageScore, max: 35 });

  // Source class factor
  const classEntry = SOURCE_CLASSES.find((c) => c.value === sourceClass);
  const classScore = classEntry ? classEntry.score : 0;
  total += classScore;
  breakdown.push({ factor: "Source class", value: classEntry?.label.split("—")[0].trim(), score: classScore, max: 25 });

  // Deployment reach factor
  const reachScore =
    deploymentCount <= 1 ? 0 : deploymentCount <= 5 ? 10 : deploymentCount <= 15 ? 20 : 30;
  total += reachScore;
  breakdown.push({ factor: "Deployment reach", value: `${deploymentCount} ${deploymentCount === 1 ? "system" : "systems"}`, score: reachScore, max: 30 });

  // Review recency factor
  const reviewScore =
    monthsSinceReview < 3 ? 0 : monthsSinceReview <= 6 ? 10 : monthsSinceReview <= 12 ? 20 : 30;
  total += reviewScore;
  breakdown.push({ factor: "Review recency", value: `${monthsSinceReview} months ago`, score: reviewScore, max: 30 });

  // PII flag
  if (hasPii) {
    total += 15;
    breakdown.push({ factor: "PII flag", value: "Yes", score: 15, max: 15 });
  }

  total = Math.min(total, 100);
  const level =
    total <= 30 ? "LOW" : total <= 55 ? "MEDIUM" : total <= 80 ? "HIGH" : "CRITICAL";

  return { total, level, breakdown };
}

const RISK_STYLES = {
  LOW: {
    color: "text-emerald-700",
    bg: "bg-emerald-50 border-emerald-200",
    action: "No immediate action required. Schedule next review within 12 months.",
  },
  MEDIUM: {
    color: "text-amber-700",
    bg: "bg-amber-50 border-amber-200",
    action: "Schedule a review within 90 days. Verify source validity and policy alignment.",
  },
  HIGH: {
    color: "text-rose-600",
    bg: "bg-rose-50 border-rose-200",
    action:
      "Review within 30 days. Consider applying a monitoring freeze pending review outcome.",
  },
  CRITICAL: {
    color: "text-rose-800",
    bg: "bg-rose-100 border-rose-300",
    action:
      "Immediate action required. Freeze deployment, initiate revocation assessment, and notify stakeholders.",
  },
};

export default function RevocationRisk() {
  useSEO({
    title: "Knowledge Artifact Revocation Risk Score — Free Tool — ProofMark",
    description:
      "Score any deployed knowledge artifact across age, source class, deployment reach, and review recency to get a revocation risk level and recommended action timeline.",
    path: "/tools/revocation-risk",
  });

  const [inputs, setInputs] = useState({
    ageMonths: 12,
    sourceClass: "2",
    deploymentCount: 3,
    monthsSinceReview: 6,
    hasPii: false,
  });

  const set = (key) => (val) => setInputs((i) => ({ ...i, [key]: val }));
  const { total, level, breakdown } = calcRisk(inputs);
  const style = RISK_STYLES[level];

  return (
    <ToolLayout>
      <div className="mb-8">
        <span className="tracked-caps">Free Tool</span>
        <h1 className="font-serif-display text-4xl mt-2 mb-3">Revocation Risk Score</h1>
        <p className="text-muted-foreground text-lg max-w-2xl">
          Score any deployed knowledge artifact across four risk factors. Get an overall risk level
          (LOW to CRITICAL) and a recommended action timeline.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Inputs */}
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium mb-1">Artifact age (months)</label>
            <input
              type="range"
              min={1}
              max={60}
              value={inputs.ageMonths}
              onChange={(e) => set("ageMonths")(Number(e.target.value))}
              className="w-full accent-brand"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>1 month</span>
              <span className="font-mono font-medium text-foreground">{inputs.ageMonths} months</span>
              <span>5 years</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Source class</label>
            <div className="space-y-2">
              {SOURCE_CLASSES.map((c) => (
                <label
                  key={c.value}
                  className={`flex items-start gap-3 cursor-pointer rounded-md px-3 py-2.5 border transition-colors text-sm ${
                    inputs.sourceClass === c.value
                      ? "border-brand bg-brand/5"
                      : "border-border hover:bg-muted/50"
                  }`}
                >
                  <input
                    type="radio"
                    name="sourceClass"
                    value={c.value}
                    checked={inputs.sourceClass === c.value}
                    onChange={() => set("sourceClass")(c.value)}
                    className="mt-0.5 accent-brand shrink-0"
                  />
                  {c.label}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Deployment reach (number of AI systems / playbooks using this artifact)
            </label>
            <input
              type="range"
              min={1}
              max={30}
              value={inputs.deploymentCount}
              onChange={(e) => set("deploymentCount")(Number(e.target.value))}
              className="w-full accent-brand"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>1</span>
              <span className="font-mono font-medium text-foreground">{inputs.deploymentCount}</span>
              <span>30+</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Months since last review</label>
            <input
              type="range"
              min={0}
              max={24}
              value={inputs.monthsSinceReview}
              onChange={(e) => set("monthsSinceReview")(Number(e.target.value))}
              className="w-full accent-brand"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>Never reviewed</span>
              <span className="font-mono font-medium text-foreground">
                {inputs.monthsSinceReview === 0 ? "just reviewed" : `${inputs.monthsSinceReview} months`}
              </span>
              <span>2 years</span>
            </div>
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={inputs.hasPii}
              onChange={(e) => set("hasPii")(e.target.checked)}
              className="accent-brand h-4 w-4"
            />
            <span className="text-sm font-medium">
              Artifact references or processes PII / sensitive data
            </span>
          </label>
        </div>

        {/* Live results */}
        <div className="space-y-4">
          <div className={`border rounded-lg p-6 ${style.bg}`}>
            <span className="tracked-caps mb-2 block">Risk level</span>
            <div className="flex items-baseline gap-3 mb-2">
              <span className={`font-serif-display text-5xl font-light ${style.color}`}>
                {level}
              </span>
              <span className={`font-mono text-2xl ${style.color}`}>{total}/100</span>
            </div>
            <p className="text-sm text-muted-foreground">{style.action}</p>
          </div>

          <div className="border border-border rounded-lg p-5 bg-card">
            <span className="tracked-caps mb-3 block">Score breakdown</span>
            <div className="space-y-3">
              {breakdown.map((b) => (
                <div key={b.factor}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-muted-foreground">{b.factor}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground/70 text-xs">{b.value}</span>
                      <span className="font-mono font-medium w-12 text-right">
                        +{b.score}/{b.max}
                      </span>
                    </div>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        b.score === 0
                          ? "bg-emerald-400"
                          : b.score <= b.max * 0.5
                          ? "bg-amber-400"
                          : "bg-rose-400"
                      }`}
                      style={{ width: `${(b.score / b.max) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <PostResultCTA
            openLoop={`Risk score ${total}/100 — ${level}. At ${level.toLowerCase()} risk, a single wrong artifact can affect hundreds of AI decisions before it's manually caught. ProofMark's revocation system freezes and traces downstream impact in under an hour.`}
            source="revocation-risk"
            nextPath="/tools/ai-governance-maturity"
            nextLabel="Next: assess your full governance maturity →"
            testimonial={null}
          />
        </div>
      </div>

      {/* Educational content */}
      <div className="mt-20 space-y-10 border-t border-border/80 pt-12">
        <section>
          <h2 className="font-serif-display text-2xl mb-3">
            When should you revoke a knowledge artifact?
          </h2>
          <p className="text-muted-foreground leading-relaxed max-w-3xl">
            An artifact should be revoked when its underlying source material is no longer valid,
            when a policy it violated is newly enforced, or when the operational context it was
            derived from has materially changed. Failing to revoke outdated artifacts means AI
            systems continue to act on stale or non-compliant knowledge — often without anyone
            noticing until an incident occurs.
          </p>
        </section>
        <section>
          <h2 className="font-serif-display text-2xl mb-3">
            The four factors that drive revocation risk
          </h2>
          <div className="space-y-3 max-w-3xl">
            {[
              { name: "Age", desc: "Knowledge derived from operational patterns has a shelf life. As processes change, older artifacts diverge from current reality. Most Class 2 artifacts have a useful life of 12–24 months without re-review." },
              { name: "Source class", desc: "Class 3 artifacts (inferred patterns) carry higher inherent risk because their provenance is indirect. Even a valid Class 3 artifact may become problematic as the underlying data distribution shifts." },
              { name: "Deployment reach", desc: "An artifact deployed to one system has contained blast radius. One deployed to 20 AI playbooks across multiple products creates organisation-wide exposure when it needs to be revoked." },
              { name: "Review recency", desc: "Regular review resets the risk clock. An 18-month-old artifact that was reviewed 2 months ago is lower risk than a 6-month-old artifact that has never been re-examined after approval." },
            ].map((item) => (
              <div key={item.name} className="border border-border rounded-lg p-4 bg-card">
                <p className="font-medium text-sm mb-1">{item.name}</p>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2 className="font-serif-display text-2xl mb-3">Building a revocation monitoring workflow</h2>
          <p className="text-muted-foreground leading-relaxed max-w-3xl">
            Effective revocation monitoring requires knowing what you have deployed, where it is
            deployed, and when it was last reviewed. Teams without this visibility typically
            discover revocation needs reactively — after a compliance finding or customer complaint.
            Proactive monitoring scores each artifact on a recurring basis and triggers review
            workflows before risk materialises. ProofMark does this automatically across your
            entire knowledge portfolio.
          </p>
        </section>
      </div>
    </ToolLayout>
  );
}
